// Copyright 2023 Sorama B.V.

import { DialogService } from "aurelia-dialog";
import { autoinject, bindable, customElement, PLATFORM } from "aurelia-framework";
import { AlertStack } from "controls/alert-stack/AlertStack";
import JSZip from "jszip";
import { FileDownloader } from "libraries/FileDownloader/FileDownloader";
import { ProgressIndicator } from "libraries/ProgressIndicator/ProgressIndicator";
import { IWorkflowModuleInstance } from "libraries/WorkflowFramework/Interfaces/IWorkflowModule";
import { BatchOperationResultModel } from "modules/dialogs/BatchOperationResult/BatchOperationResultDialog";
import { ConfirmationModel } from "modules/dialogs/Confirmation/ConfirmationDialog";
import { WorkflowModeService } from "services/WorkflowModes/WorkflowModeService";
import { IFileTreeContainer, NodeModel } from "./TreeNode";

interface IProgressState {
    current: number;
    total: number;
    unit: string;
    increment(): void;
}

class FileOperationProgress implements IProgressState {
    current = 0;
    total = 0;
    unit = "files";

    increment() {
        this.current++;
    }
}

@customElement("file-tree")
@autoinject
export class FileTree implements IFileTreeContainer {
    @bindable public title: string;

    @bindable public root: NodeModel;

    @bindable({ changeHandler: "refresh" })
    public refreshAndGetTreeRoot: () => Promise<NodeModel>;
    private isRefreshing = false;

    @bindable public downloadFile: (event: {
        file: NodeModel;
        downloadProgressCallback?: (progress: ProgressIndicator) => void;
        directly: boolean;
    }) => Promise<Blob>;
    private isDownloading = false;

    @bindable public deleteFile: (file: NodeModel) => Promise<void>;
    private isDeleting = false;

    @bindable public editFile: (file: NodeModel) => Promise<void>;
    public isEditing = false;

    @bindable public generateReport: () => Promise<void>;
    @bindable public busyLoadingFiles = false;

    @bindable
    public showFileType: boolean;

    @bindable public importFile: (event: {
        file: NodeModel;
        importProgressCallback?: (progress: ProgressIndicator) => void;
    }) => Promise<void>;
    private isImporting = false;

    @bindable public canImportSorx = false;

    public get numberOfSelectedFiles(): number {
        return this.root.selectedFiles.length;
    }

    public get canEdit(): boolean {
        return !this.isEditing && this.hasFilesSelected && !!this.editFile && !this.busyLoadingFiles;
    }

    public get canDownload(): boolean {
        return (
            this.hasFilesSelected &&
            !this.isDownloading &&
            this.root.selectedFiles.every((file) => file.canBeDownloaded)
        );
    }

    public get canImport(): boolean {
        return (
            this.hasFilesSelected &&
            !this.isImporting &&
            this.root.selectedFiles.every((file) => file.canBeImported || (this.canImportSorx && file.type === "SorX"))
        );
    }

    public get canGenerate(): boolean {
        return this.hasFilesSelected && !this.isEditing && !!this.generateReport && !this.busyLoadingFiles;
    }

    public get canDelete(): boolean {
        return this.hasFilesSelected && !this.isDeleting && this.root.selectedFiles.every((file) => file.canBeDeleted);
    }

    // Whether import and download operations will be done in sequence or in parallel
    @bindable public performOperationsInParallel = true;

    @bindable public showRefreshButton = false;

    @bindable public moduleInstance: IWorkflowModuleInstance;

    protected downloadProgress: IProgressState;
    protected importProgress: IProgressState;

    private failedImports: string[] = [];

    constructor(
        private element: Element,
        private readonly dialogService: DialogService,
        private readonly workflowModeService: WorkflowModeService
    ) {}

    private alertStack: AlertStack;
    private alertCloseTimeoutSeconds = 0;
    private isIndustrial = true;

    private closingProgressBar = false;
    private get progressTransitionTimeSeconds() {
        if (this.closingProgressBar) return 1;
        return 5;
    }

    async bind() {
        if (!this.root) {
            this.refresh();
        }
        this.isIndustrial = this.workflowModeService.selectedWorkflowMode.isIndustrial;
    }

    public async refresh() {
        this.isRefreshing = true;

        try {
            if (this.refreshAndGetTreeRoot) {
                this.root = await this.refreshAndGetTreeRoot();
            }
        } finally {
            this.isRefreshing = false;
        }

        if (!this.root) {
            return;
        }

        // Make sure that buttons on the file nodes can call the methods in this class (IFileTreeContainer)
        this.root.subTreeAsList.forEach((x) => (x.container = this));
    }

    private get hasFilesSelected(): boolean {
        return this.root && this.numberOfSelectedFiles > 0;
    }

    private get hasDisabledFiles(): boolean {
        return this.root && this.root.subTreeAsList.filter((x) => x.isDisabled).length != 0;
    }

    public get subTreeAsList(): NodeModel[] {
        return this.root ? this.root.subTreeAsList : [];
    }

    public async download(
        file: NodeModel,
        downloadProgressCallback?: (progress: ProgressIndicator) => void,
        directly?: boolean
    ): Promise<Blob> {
        this.isDownloading = true;
        return this.downloadFile({ file, downloadProgressCallback, directly }).catch((e) => {
            this.alertStack.pushError(
                "Failed to download '" + file.path + "': " + e.message,
                this.alertCloseTimeoutSeconds
            );
            return null;
        });
    }

    public async import(
        file: NodeModel,
        importProgressCallback?: (progress: ProgressIndicator) => void
    ): Promise<void> {
        this.isImporting = true;
        await this.importFile({ file, importProgressCallback }).catch((e) => {
            this.alertStack.pushError(
                "Failed to import '" + file.path + "': " + e.message,
                this.alertCloseTimeoutSeconds
            );
            this.failedImports.push(file.path);
        });
    }

    public async delete(file: NodeModel): Promise<void> {
        this.isDeleting = true;
        await this.deleteFile(file).catch((e) => {
            this.alertStack.pushError(
                "Failed to delete '" + file.path + "': " + e.message,
                this.alertCloseTimeoutSeconds
            );
        });
        this.refresh();
    }

    public async edit(file: NodeModel): Promise<void> {
        this.isEditing = true;
        await this.editFile(file).catch((e) => {
            this.alertStack.pushError(
                "Failed to edit '" + file.path + "': " + e.message,
                this.alertCloseTimeoutSeconds
            );
        });
        this.isEditing = false;
    }

    public disableFile(node: NodeModel, isDisabled = false): void {
        this.root.subTreeAsList
            .filter((x) => x.path === node.path)
            .forEach((x) => {
                x.isDisabled = isDisabled;
                x.isChecked = false; // Uncheck the file when disabling it
            });
    }

    private async downloadSelected(): Promise<void> {
        this.downloadProgress = this.initializeProgressState();
        await this.operateOnSelected(this.download, this.downloadProgress);
    }

    private async importSelected(): Promise<void> {
        this.importProgress = this.initializeProgressState();
        await this.operateOnSelected(this.import, this.importProgress);
    }

    private async editSelected(): Promise<void> {
        await this.operateOnSelected(this.edit, null);
    }

    private initializeProgressState(): FileOperationProgress {
        const progress = new FileOperationProgress();
        progress.total = this.numberOfSelectedFiles;
        return progress;
    }

    private async generateSelected(): Promise<void> {
        await this.generateReport().catch((e) => {
            this.alertStack.pushError(
                "Failed to generate the report." + " " + e.message,
                this.alertCloseTimeoutSeconds
            );
        });
    }
    public async deleteSelected(): Promise<void> {
        const confirmationModel: ConfirmationModel = {
            title: "Delete Files",
            text: "Are you sure you want to delete the selected file(s)?",
            confirmText: "Delete",
            operation: () => this.operateOnSelected(this.delete, null),
        };

        this.dialogService.open({
            viewModel: PLATFORM.moduleName("modules/dialogs/Confirmation/ConfirmationDialog"),
            model: confirmationModel,
            lock: true,
        });
    }

    // Generic function for performing operations on multiple files
    private async operateOnSelected(
        operation: (file: NodeModel) => Promise<void | Blob>,
        progress: IProgressState
    ): Promise<void> {
        if (this.performOperationsInParallel) {
            await this.parallelOperation(operation, progress);
        } else {
            await this.sequentialOperation(operation, progress);
        }

        this.closeProgressBarAnimation();
    }

    private closeProgressBarAnimation() {
        setTimeout(() => {
            if (this.isImporting) this.confirmSuccessfulImports();

            this.failedImports = [];
            this.isDeleting = false;
            this.isEditing = false;
            this.isDownloading = false;
            this.isImporting = false;
        }, 1000);
    }

    private confirmSuccessfulImports() {
        let batchResultModel: BatchOperationResultModel;

        // If all imports failed, don't show the success message
        if (this.failedImports.length === this.numberOfSelectedFiles) return;

        // Show success message
        batchResultModel = {
            title: "Success!",
            text:
                this.failedImports.length === 0
                    ? "All files were imported successfully"
                    : `Imported ${this.numberOfSelectedFiles - this.failedImports.length} out of ${this.numberOfSelectedFiles} files successfully.`,
        };

        this.dialogService.open({
            viewModel: PLATFORM.moduleName("modules/dialogs/BatchOperationResult/BatchOperationResultDialog"),
            model: batchResultModel,
            lock: true,
        });
    }

    /**
     * Generates a zip file from added files and triggers the download
     * @param zip The JSZip instance containing files to be zipped
     * @param progressBar The progress bar to update during operation
     * @returns Promise that resolves when zip is generated and download started
     */
    private async generateAndDownloadZip(zip: JSZip): Promise<void> {
        if (Object.keys(zip.files).length) {
            // Only return a zip if we actually added files - use memory-efficient options
            await zip
                .generateAsync({
                    type: "blob",
                    compression: "DEFLATE",
                    compressionOptions: { level: 3 }, // Lower compression level (1-9) uses less memory
                    streamFiles: true, // Process files one by one to reduce memory usage
                })
                .then((content) => {
                    FileDownloader.downloadBlobAsFile(content, "application/zip", "cam-iv64-files");
                });
        }
    }

    private async parallelOperation(
        operation: (file: NodeModel) => Promise<void | Blob>,
        progress: IProgressState | null
    ) {
        if (operation === this.download && this.numberOfSelectedFiles > 1) {
            const zip = new JSZip();
            const downloadPromises = this.root.selectedFiles.map((file) =>
                this.download
                    .call(this, file, progress, false)
                    .then((res: Blob) => {
                        if (res) {
                            const fileName = file.extension === "json" ? file.name + ".pdf" : file.nameAndExtension;
                            this.safelyAddToZip(zip, fileName, res);
                        }
                    })
                    .finally(() => progress?.increment())
            );

            await Promise.all(downloadPromises)
                .then(async () => {
                    this.generateAndDownloadZip(zip);
                })
                .finally(() => (this.isDownloading = false));
        } else {
            const operationPromises = this.root.selectedFiles.map((file) =>
                operation.call(this, file, progress, true).finally(() => progress?.increment())
            );
            await Promise.all(operationPromises);
        }
    }

    private async sequentialOperation(
        operation: (file: NodeModel) => Promise<void | Blob>,
        progress: IProgressState | null
    ) {
        if (operation === this.download && this.numberOfSelectedFiles > 1) {
            const zip = new JSZip();
            for (const file of this.root.selectedFiles) {
                await this.download
                    .call(this, file, progress, false)
                    .then(async (res: Blob) => {
                        if (res) {
                            const fileName = file.extension === "json" ? file.name + ".pdf" : file.nameAndExtension;
                            await this.safelyAddToZip(zip, fileName, res);
                        }
                    })
                    .finally(() => progress?.increment());
            }
            this.generateAndDownloadZip(zip);
        } else {
            for (const file of this.root.selectedFiles) {
                await operation.call(this, file, progress, true).finally(() => progress?.increment());
            }
        }
    }

    /**
     * Safely adds a file to the ZIP archive with memory usage considerations
     * @param zip The JSZip instance
     * @param fileName The name to use in the zip archive
     * @param blob The blob data to add
     * @returns Promise that resolves when the file is added
     */
    private async safelyAddToZip(zip: JSZip, fileName: string, blob: Blob): Promise<void> {
        if (!blob || blob.size <= 0) {
            return;
        }

        try {
            // Use memory-efficient options for all files
            zip.file(fileName, blob, {
                compression: "DEFLATE",
                compressionOptions: {
                    level: 3, // Lower compression level uses less memory
                },
            });
        } catch (e) {
            this.alertStack.pushError(
                `Failed to add '${fileName}' to zip: ${e.message}`,
                this.alertCloseTimeoutSeconds
            );
            // If we can't add it to the ZIP, we'll just log the error but continue with other files
        }
    }

    public static findCommonRootPath(fullPaths: string[]): string {
        const prefixFolders = [];
        const folderList = fullPaths[0].split("/");
        for (const folder of folderList) {
            prefixFolders.push(folder);
            const tmpPrefix = prefixFolders.join("/");
            // For the code `fullPath.charAt(tmpPrefix.length) == '/' `,
            // we are making sure that folders such as a/b/c and a/b/ccc does not have same prefix as a/b/c
            const prefixOfAll = fullPaths.every(
                (fullPath) => fullPath.startsWith(tmpPrefix) && fullPath.charAt(tmpPrefix.length) == "/"
            );
            if (!prefixOfAll) {
                prefixFolders.pop();
                break;
            }
        }
        return prefixFolders.join("/");
    }

    public static createTreeFromPaths(fullPaths: string[]): NodeModel | void {
        if (!fullPaths || fullPaths.length === 0) return;

        // Look for common root path (e.g. "/data/UserData")
        const commonRootPath = FileTree.findCommonRootPath(fullPaths);

        const folderTreeRoot = new NodeModel(commonRootPath, true);
        const paths = fullPaths.map((x) => (commonRootPath ? x.replace(commonRootPath + "/", "") : x));
        paths.forEach((path) => {
            path.split("/").reduce((parent, nodeName) => {
                const currentPath = parent.path + "/" + nodeName;
                const nodeThatAlreadyExists = parent.children.find((child) => child.path == currentPath);
                if (nodeThatAlreadyExists) {
                    return nodeThatAlreadyExists;
                } else {
                    // If we haven't created the node, create it
                    const currentNodeModel = new NodeModel(currentPath);
                    parent.children.push(currentNodeModel);
                    return currentNodeModel;
                }
            }, folderTreeRoot);
        });
        return folderTreeRoot;
    }

    public onNodeCheckChange(node: NodeModel, isChecked: boolean): void {
        const dispatch = (targetNode: NodeModel) => {
            const customEvent = new CustomEvent("file-checked", {
                detail: { node: targetNode, isChecked },
                bubbles: true,
            });
            this.element.dispatchEvent(customEvent);
        };

        // A file (leaf node) was checked
        if (!node.children.length) {
            dispatch(node);
            return;
        }

        // A folder with multiple files was checked - recurse through all descendant files
        node.subTreeAsList
            .filter((x) => x.isFile)
            .forEach((file) => {
                // isChecked will trigger this (onNodeCheckChange) function for each file, so no need to dispatch here
                file.isChecked = isChecked;
            });
    }

    public onNodeFocusChange(node: NodeModel, isFocused: boolean): void {
        const dispatch = (targetNode: NodeModel) => {
            const customEvent = new CustomEvent("file-focused", {
                detail: { node: targetNode, isFocused },
                bubbles: true,
            });
            this.element.dispatchEvent(customEvent);
        };

        dispatch(node);

        if (isFocused) {
            // Unfocus all other nodes
            this.root.subTreeAsList.forEach((n) => {
                if (n !== node && n.isFocused) {
                    n.isFocused = false;
                }
            });
        }
    }

    protected onAllChecked(isChecked: boolean): boolean {
        this.root.subTreeAsList.forEach((element) => {
            element.isChecked = isChecked;
        });
        //because we use click delegate
        return true;
    }
}
